// The presentation vocabulary for the two scales this site publishes.
//
// Kept apart from src/lib/states.ts on purpose: route head() functions, the map
// legend, and the OG renderer all need the labels and colours but none of them need
// 51 states of prose, and states.ts is ~60KB. Everything here is tokens or plain
// strings — no component imports, so it is cheap to pull in anywhere.
//
// Two scales, never conflated (see the data-integrity policy in README.md):
//   RiskLevel       — how widely the Lone Star tick is REPORTED in an area.
//   ConfidenceLevel — how well documented that reporting is.
// Neither is a personal medical risk, and neither is official surveillance.

export type RiskLevel = "low" | "emerging" | "established" | "high";

export type ConfidenceLevel = "reported" | "established" | "emerging" | "limited";

/** Low -> high. Legends and selects iterate this so the order is defined in one place. */
export const RISK_LEVELS: readonly RiskLevel[] = ["low", "emerging", "established", "high"];

export const CONFIDENCE_LEVELS: readonly ConfidenceLevel[] = [
  "established",
  "reported",
  "emerging",
  "limited",
];

/**
 * Short labels for badges, the legend and list rows. Written for someone who has never
 * read a surveillance report: V3 shipped "Established" and "High Awareness", which
 * made readers ask what the difference was and which one was worse.
 */
export const riskLabel: Record<RiskLevel, string> = {
  low: "Rarely reported",
  emerging: "Spreading",
  established: "Found here",
  high: "Alpha-gal hotspot",
};

/**
 * One sentence per level, short enough to sit inline in the legend so nobody has to
 * scroll to find out what a colour means. Wording tracks the CDC range and AGS
 * distribution sources every state entry cites; it never states personal risk.
 */
export const riskMeaning: Record<RiskLevel, string> = {
  low: "Few or no Lone Star tick reports in public data. Other ticks still live here.",
  emerging: "Reports are increasing at the edge of the Lone Star tick's range.",
  established: "Inside the Lone Star tick's documented range.",
  high: "Inside the range, and among the states where CDC found the highest rates of suspected alpha-gal cases.",
};

/**
 * The direct answer to "are Lone Star ticks where I live?", as a headline. Same facts as
 * riskMeaning, phrased as a reply rather than a definition.
 */
export function riskHeadline(level: RiskLevel, place: string): string {
  switch (level) {
    case "low":
      return `Lone Star ticks are rarely reported in ${place}.`;
    case "emerging":
      return `Lone Star ticks are starting to show up in ${place}.`;
    case "established":
      return `Yes. Lone Star ticks live in ${place}.`;
    case "high":
      return `Yes. Lone Star ticks live in ${place}, and it's an alpha-gal hotspot.`;
  }
}

/** What to do about it, in one sentence. Prevention only — never a statement of personal risk. */
export const riskAdvice: Record<RiskLevel, string> = {
  low: "Other tick species live here too, so the same habits apply: repellent outdoors and a tick check when you come in.",
  emerging:
    "Use repellent outdoors and check yourself, your kids and your pets for ticks when you come in.",
  established:
    "Use repellent outdoors and check yourself, your kids and your pets for ticks when you come in.",
  high: "Use repellent and do tick checks. If you ever get hives or stomach pain hours after eating red meat, mention tick bites to a clinician.",
};

/** Badge/chip classes. Token pairs only — every one is contrast-checked in styles.contrast.test.ts. */
export const riskClass: Record<RiskLevel, string> = {
  low: "bg-risk-low text-risk-low-foreground",
  emerging: "bg-risk-emerging text-risk-emerging-foreground",
  established: "bg-risk-established text-risk-established-foreground",
  high: "bg-risk-high text-risk-high-foreground",
};

/** CSS custom property holding each level's fill — what the map paints with. */
export const riskVar: Record<RiskLevel, string> = {
  low: "var(--risk-low)",
  emerging: "var(--risk-emerging)",
  established: "var(--risk-established)",
  high: "var(--risk-high)",
};

/**
 * How strongly the risk scale outlines each segment, as a share of the ink colour. The
 * palest level is close to the card it sits on, so the outline is what keeps every step
 * of the scale visible; styles.contrast.test.ts checks the result in both themes.
 */
export const RISK_SCALE_EDGE_ALPHA = 0.35;

/**
 * "We have no usable public data for this area." Deliberately its own neutral, never
 * the low-risk green: an absence of reports is not evidence of absence.
 */
export const NO_DATA = {
  label: "Limited public data",
  meaning: "No usable public data for this area. That is not the same as low risk.",
  class: "bg-risk-none text-risk-none-foreground",
  var: "var(--risk-none)",
} as const;

/**
 * Flat hex per level for the OG image renderer, which builds raw SVG on the server
 * with no stylesheet to resolve custom properties against. Kept in sync by eye with
 * the light-theme ramp above; og-image.test.ts asserts every level has one.
 */
export const riskHex: Record<RiskLevel, string> = {
  low: "#cde7c8",
  emerging: "#e7be62",
  established: "#dc813b",
  high: "#af2c1f",
};

/** Text colour to pair with each riskHex fill. Contrast-checked in og-image.test.ts. */
export const riskHexForeground: Record<RiskLevel, string> = {
  low: "#16281d",
  emerging: "#16281d",
  established: "#16281d",
  high: "#ffffff",
};

export const NO_DATA_HEX = "#dbdee2";
export const NO_DATA_HEX_FOREGROUND = "#16281d";

/** Brand green used by the OG renderer when a card has no risk level at all. */
export const OG_NEUTRAL_HEX = "#164e33";
export const OG_NEUTRAL_HEX_FOREGROUND = "#ffffff";
